import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, ExternalLink, AlertTriangle } from 'lucide-react';
import Header from '../components/Header.jsx';
import Footer from '../components/Footer.jsx';
import { api } from '../api/client.js';

function resolveAnswer(question, value) {
  if (value === null || value === undefined || value === '') return null;
  if (question.type === 'flag') {
    if (value === true) return 'Oui';
    if (value === false) return 'Non';
    return null;
  }
  if (question.type === 'select') {
    const option = question.options?.find((o) => o.value === value);
    return option?.label || String(value);
  }
  if (question.type === 'multiselect') {
    if (!Array.isArray(value) || value.length === 0) return null;
    const labels = value
      .map((v) => {
        const option = question.options?.find((o) => o.value === v);
        return option?.label || String(v);
      })
      .filter(Boolean);
    return labels.length > 0 ? labels : null;
  }
  if (question.type === 'text' || question.type === 'textarea' || question.type === 'number') {
    const str = String(value).trim();
    return str.length > 0 ? str : null;
  }
  return null;
}

function formatDateLong(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function shortId(id) {
  if (!id) return '';
  const s = String(id);
  return s.split('-')[0] || s.slice(0, 8);
}

export default function AvaloirDetail() {
  const { id } = useParams();
  const nav = useNavigate();

  const [avaloir, setAvaloir] = useState(null);
  const [schema, setSchema] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgErr, setImgErr] = useState(false);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const [avaloirData, schemaData] = await Promise.all([
          api.get(`/api/avaloirs/${id}`),
          api.get('/api/survey/schema'),
        ]);
        if (cancel) return;
        setAvaloir(avaloirData);
        setSchema(schemaData);
      } catch (e) {
        if (!cancel) {
          if (e.status === 404) {
            setError('Cet avaloir est introuvable.');
          } else {
            setError(e.message || 'Erreur de chargement.');
          }
        }
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [id]);

  if (loading) {
    return (
      <div className="vrt-detail">
        <Header />
        <main className="vrt-detail__main">
          <div className="vrt-detail__loading">Chargement…</div>
        </main>
        <Footer />
        <style>{detailStyles}</style>
      </div>
    );
  }

  if (error || !avaloir) {
    return (
      <div className="vrt-detail">
        <Header />
        <main className="vrt-detail__main">
          <div className="vrt-detail__error" role="alert">
            <AlertTriangle size={20} />
            <span>{error || 'Avaloir introuvable.'}</span>
          </div>
          <div className="vrt-detail__error-actions">
            <Link to="/map" className="vrt-btn vrt-btn--teal">
              Retour à la carte
            </Link>
          </div>
        </main>
        <Footer />
        <style>{detailStyles}</style>
      </div>
    );
  }

  const a = avaloir;
  const questions = schema?.questions || [];
  const sortedQuestions = [...questions].sort(
    (q1, q2) => (q1.position || 0) - (q2.position || 0)
  );
  const hasCoords = Number.isFinite(a.latitude) && Number.isFinite(a.longitude);
  const gmaps = hasCoords
    ? `https://www.google.com/maps?q=${a.latitude},${a.longitude}`
    : null;
  const dateLong = formatDateLong(a.created_at);
  const titleId = shortId(a.id);

  return (
    <div className="vrt-detail">
      <Header />

      <main className="vrt-detail__main">
        {/* Back button */}
        <button
          type="button"
          className="vrt-detail__back"
          onClick={() => nav(-1)}
          aria-label="Retour"
        >
          <ArrowLeft size={18} />
          <span>Retour</span>
        </button>

        {/* Photo principale */}
        <div className="vrt-detail__photo-wrap">
          {a.photo_url && !imgErr ? (
            <a
              href={a.photo_url}
              target="_blank"
              rel="noreferrer"
              className="vrt-detail__photo-link"
            >
              <img
                src={a.photo_url}
                alt={`Avaloir ${titleId}`}
                onError={() => setImgErr(true)}
                className="vrt-detail__photo"
              />
            </a>
          ) : (
            <div className="vrt-detail__photo-placeholder">
              {a.photo_url ? 'Photo indisponible' : 'Pas de photo'}
            </div>
          )}
        </div>

        {/* 2ème photo si présente */}
        {a.photo_url_2 && (
          <div className="vrt-detail__photo-wrap" style={{ marginTop: 12 }}>
            <a
              href={a.photo_url_2}
              target="_blank"
              rel="noreferrer"
              className="vrt-detail__photo-link"
            >
              <img
                src={a.photo_url_2}
                alt={`Avaloir ${titleId} — photo 2`}
                className="vrt-detail__photo"
              />
            </a>
          </div>
        )}

        {/* Header */}
        <div className="vrt-detail__header">
          <h1 className="vrt-detail__title">Avaloir {a.code || `#${titleId}`}</h1>
          <div className="vrt-detail__subtitle">
            {a.city && <span>{a.city}</span>}
            {a.city && dateLong && <span className="vrt-detail__sep"> · </span>}
            {dateLong && <span>{dateLong}</span>}
          </div>
          {a.priority_life && (
            <div className="vrt-detail__priority">
              <AlertTriangle size={16} />
              <span>PRIORITÉ ABSOLUE LIFE</span>
            </div>
          )}
        </div>

        {/* Localisation */}
        <section className="vrt-detail__section">
          <h2 className="vrt-detail__section-title">
            <MapPin size={12} /> Localisation
          </h2>
          {hasCoords ? (
            <>
              <div className="vrt-detail__coords">
                {a.latitude.toFixed(6)}, {a.longitude.toFixed(6)}
              </div>
              {a.city && (
                <div className="vrt-detail__field">
                  <div className="vrt-detail__field-label">Ville</div>
                  <div className="vrt-detail__field-value">
                    {a.city}
                    {a.city_code && (
                      <span className="vrt-detail__city-code"> ({a.city_code})</span>
                    )}
                  </div>
                </div>
              )}
              <a
                href={gmaps}
                target="_blank"
                rel="noreferrer"
                className="vrt-btn vrt-btn--teal vrt-detail__maps-btn"
              >
                <ExternalLink size={16} />
                Ouvrir dans Google Maps
              </a>
            </>
          ) : (
            <div className="vrt-detail__field-value vrt-detail__muted">
              Coordonnées non disponibles
            </div>
          )}
        </section>

        {/* Réponses au formulaire */}
        <section className="vrt-detail__section">
          <h2 className="vrt-detail__section-title">Réponses au formulaire</h2>
          {a.answers && Object.keys(a.answers).length > 0 ? (
            <div className="vrt-detail__answers">
              {sortedQuestions.map((question) => {
                const value = a.answers?.[question.key];
                if (value === undefined) return null;
                const resolved = resolveAnswer(question, value);
                if (resolved === null) return null;

                return (
                  <div key={question.key} className="vrt-detail__answer">
                    <div className="vrt-detail__answer-label">
                      {question.label}
                    </div>
                    <div className="vrt-detail__answer-value">
                      {Array.isArray(resolved) ? (
                        <div className="vrt-detail__chips">
                          {resolved.map((label, i) => (
                            <span key={i} className="vrt-detail__chip">
                              {label}
                            </span>
                          ))}
                        </div>
                      ) : question.type === 'flag' ? (
                        <span
                          className={`vrt-detail__chip ${
                            resolved === 'Oui'
                              ? 'vrt-detail__chip--yes'
                              : 'vrt-detail__chip--no'
                          }`}
                        >
                          {resolved}
                        </span>
                      ) : (
                        <span>{resolved}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="vrt-detail__field-value vrt-detail__muted">
              Aucune réponse enregistrée.
            </div>
          )}
        </section>

        {/* Métadonnées */}
        <section className="vrt-detail__section vrt-detail__section--last">
          <h2 className="vrt-detail__section-title">Métadonnées</h2>
          <div className="vrt-detail__field">
            <div className="vrt-detail__field-label">Identifiant complet</div>
            <div className="vrt-detail__field-value vrt-detail__mono">{a.id}</div>
          </div>
          {dateLong && (
            <div className="vrt-detail__field">
              <div className="vrt-detail__field-label">Date de création</div>
              <div className="vrt-detail__field-value">{dateLong}</div>
            </div>
          )}
        </section>
      </main>

      <Footer />

      <style>{detailStyles}</style>
    </div>
  );
}

const detailStyles = `
  .vrt-detail { min-height: 100vh; background: var(--vrt-bg); display: flex; flex-direction: column; }
  .vrt-detail__main {
    flex: 1;
    max-width: 720px;
    width: 100%;
    margin: 0 auto;
    padding: 16px 20px 40px;
  }
  .vrt-detail__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: transparent;
    border: none;
    padding: 8px 4px;
    color: var(--vrt-text-muted);
    font-size: 14px;
    font-weight: 500;
    margin-bottom: 16px;
    cursor: pointer;
  }
  .vrt-detail__back:hover { color: var(--vrt-navy); }

  .vrt-detail__loading {
    padding: 40px;
    text-align: center;
    color: var(--vrt-text-muted);
    background: var(--vrt-bg-alt);
    border-radius: var(--vrt-radius);
  }
  .vrt-detail__error {
    display: flex;
    align-items: center;
    gap: 10px;
    background: #fdecea;
    color: var(--vrt-danger);
    padding: 14px 16px;
    border-radius: var(--vrt-radius);
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 16px;
  }
  .vrt-detail__error-actions {
    display: flex;
    justify-content: center;
  }

  .vrt-detail__photo-wrap {
    background: #000;
    border-radius: 12px;
    overflow: hidden;
    margin-bottom: 20px;
    box-shadow: 0 2px 12px rgba(0,0,0,0.08);
    display: flex;
    align-items: center;
    justify-content: center;
    max-height: 400px;
  }
  .vrt-detail__photo-link {
    display: block;
    width: 100%;
    line-height: 0;
  }
  .vrt-detail__photo {
    display: block;
    width: 100%;
    max-height: 400px;
    object-fit: contain;
    background: #000;
  }
  .vrt-detail__photo-placeholder {
    width: 100%;
    height: 240px;
    background: #e9ecef;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #8a93a3;
    font-size: 14px;
  }

  .vrt-detail__header {
    margin-bottom: 28px;
  }
  .vrt-detail__title {
    margin: 0 0 6px;
    color: var(--vrt-navy);
    font-size: 26px;
    font-weight: 700;
    line-height: 1.2;
  }
  .vrt-detail__subtitle {
    color: var(--vrt-text-muted);
    font-size: 15px;
    margin-bottom: 12px;
  }
  .vrt-detail__sep { opacity: 0.6; }
  .vrt-detail__priority {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    background: var(--vrt-danger);
    color: #fff;
    padding: 6px 12px;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 0.04em;
  }

  .vrt-detail__section {
    padding: 24px 0 28px;
    border-bottom: 1px solid var(--vrt-border);
  }
  .vrt-detail__section--last {
    border-bottom: none;
  }
  .vrt-detail__section-title {
    margin: 0 0 16px;
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--vrt-navy);
    font-weight: 700;
    padding-bottom: 8px;
    border-bottom: 1px solid var(--vrt-navy);
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }

  .vrt-detail__coords {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 15px;
    color: var(--vrt-navy);
    background: var(--vrt-bg-alt);
    padding: 10px 14px;
    border-radius: 8px;
    margin-bottom: 12px;
    word-break: break-all;
  }
  .vrt-detail__city-code {
    color: var(--vrt-text-muted);
    font-size: 13px;
  }
  .vrt-detail__maps-btn {
    margin-top: 12px;
  }

  .vrt-detail__field {
    margin-bottom: 12px;
  }
  .vrt-detail__field:last-child { margin-bottom: 0; }
  .vrt-detail__field-label {
    font-size: 12px;
    color: var(--vrt-text-muted);
    font-weight: 600;
    margin-bottom: 4px;
  }
  .vrt-detail__field-value {
    font-size: 15px;
    color: var(--vrt-text);
  }
  .vrt-detail__muted {
    color: var(--vrt-text-muted);
    font-style: italic;
  }
  .vrt-detail__mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 13px;
    background: var(--vrt-bg-alt);
    padding: 8px 12px;
    border-radius: 6px;
    word-break: break-all;
    user-select: all;
  }

  .vrt-detail__answers {
    display: flex;
    flex-direction: column;
    gap: 14px;
  }
  .vrt-detail__answer {
    padding: 0;
  }
  .vrt-detail__answer-label {
    font-weight: 700;
    color: var(--vrt-navy);
    font-size: 14px;
    margin-bottom: 6px;
  }
  .vrt-detail__answer-value {
    color: var(--vrt-text);
    font-size: 15px;
    line-height: 1.4;
    word-break: break-word;
  }
  .vrt-detail__chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
  }
  .vrt-detail__chip {
    display: inline-block;
    background: var(--vrt-teal-100);
    color: var(--vrt-teal-700);
    padding: 4px 10px;
    border-radius: 999px;
    font-size: 13px;
    font-weight: 600;
  }
  .vrt-detail__chip--yes {
    background: var(--vrt-teal-100);
    color: var(--vrt-teal-700);
  }
  .vrt-detail__chip--no {
    background: #f0f1f4;
    color: var(--vrt-text-muted);
  }

  @media (max-width: 480px) {
    .vrt-detail__main { padding: 12px 16px 32px; }
    .vrt-detail__title { font-size: 22px; }
    .vrt-detail__photo { max-height: 320px; }
  }
`;
